import React, { useMemo } from 'react';
import clsx from 'clsx';
import { titleLines, type KitFontId, type ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../common/types';
import { ThemeStillLayout, themeStillVars } from './themeStillVars';
/* eslint-disable css-modules/no-unused-class -- the theme classes are picked by id at run time */
import styles from './ThemeStill.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The class of each theme. The id of Noir is `dark`; its class is `noir` (design q). */
const THEME_CLASS: Readonly<Record<ReleaseThemeId, string | undefined>> = {
  clean: styles.clean,
  hype: styles.hype,
  dreamy: styles.dreamy,
  dark: styles.noir,
  warm: styles.warm,
  retro: styles.retro,
  neon: styles.neon,
  handmade: styles.handmade,
};

/** The texture of each theme (design `tile`: grain, paper, or none). */
function textureClass(themeId: ReleaseThemeId): string | undefined {
  switch (themeId) {
    case 'dark':
    case 'retro':
    case 'warm':
      return styles.grain;
    case 'handmade':
      return styles.paper;
    case 'clean':
    case 'hype':
    case 'dreamy':
    case 'neon':
      return undefined;
  }
}

const LAYOUT_CLASS: Readonly<Record<ThemeStillLayout, string | undefined>> = {
  card: undefined,
  ground: styles.groundLayout,
  type: styles.typeLayout,
};

export interface ThemeStillProps {
  themeId: ReleaseThemeId;
  fontOverride: KitFontId | null;
  palette: readonly string[];
  artworkUrl: string | undefined;
  title: string;
  artist: string;
  kicker: string;
  /** Where the cover goes. Default: the cover card of the theme. */
  layout?: ThemeStillLayout;
  /** 9:16 (a story) instead of 4:5. */
  tall?: boolean;
  /** The 26 px chip and the 56 px card: only the ground, the cover and the title. */
  mini?: boolean;
  className?: string;
  testId?: string;
}

/**
 * A still of a song in a release theme (design q2 "Your song in <Theme>", q3c, q5): drawn in the
 * browser from the song's cover, its palette shaped by the theme (`themeColours`), and the type of
 * the look (`themeType`). It is the still state of the theme. The real assets come from the
 * renderer, so a still is a sketch, not the asset.
 */
const ThemeStillComponent: React.FC<ThemeStillProps> = ({
  themeId,
  fontOverride,
  palette,
  artworkUrl,
  title,
  artist,
  kicker,
  layout = 'card',
  tall = false,
  mini = false,
  className,
  testId,
}) => {
  const vars = useMemo(
    () => themeStillVars({ themeId, fontOverride, palette, artworkUrl, title, layout }),
    [artworkUrl, fontOverride, layout, palette, themeId, title]
  );
  const texture = textureClass(themeId);
  // The second title line in the accent (Hype, Neon): the shared rule of the renderer. The mini
  // still (26 px chip) shows one letter and no accent line, as in design q3a.
  const lines = mini ? { first: title, second: null } : titleLines(themeId, title);
  return (
    <div
      className={clsx(
        styles.tt,
        THEME_CLASS[themeId],
        LAYOUT_CLASS[layout],
        tall && styles.s916,
        mini && styles.mini,
        className
      )}
      style={vars}
      aria-hidden="true"
      data-theme-id={themeId}
      data-testid={testId}
    >
      <i className={clsx(styles.g, themeId === 'handmade' && styles.paper)} />
      <i className={styles.x} />
      <i className={styles.y} />
      <i className={styles.c} />
      <span className={styles.k}>{kicker}</span>
      <span className={styles.t}>
        {lines.first}
        {lines.second !== null && (
          <span className={styles.t2} data-testid="theme-still-accent-line">
            {lines.second}
          </span>
        )}
      </span>
      <span className={styles.a}>{artist}</span>
      <i className={clsx(styles.tex, texture)} />
    </div>
  );
};

export const ThemeStill = TypedMemo(ThemeStillComponent);
